import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { CartService } from '../../services/cart.service';
import { ConfigService } from '../../services/config.service';

@Component({
  selector: 'app-public-business',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './public-business.component.html',
  styleUrl: './public-business.component.scss'
})
export class PublicBusinessComponent implements OnInit {
  business: any = null;
  loading = true;
  error = '';
  isLoggedIn = false;
  slug = '';
  menuAvailable = false;
  menuLoading = false;
  private menuData: any = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private authService: AuthService,
    private cartService: CartService,
    private configService: ConfigService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.isLoggedIn = !!this.authService.getToken();
    this.slug = this.route.snapshot.paramMap.get('slug') || '';
    if (this.slug) {
      this.loadBusiness(this.slug);
    } else {
      this.error = 'Local no encontrado';
      this.loading = false;
    }
  }

  private loadBusiness(slug: string): void {
    this.http.get<any>(`/api/v1/public/local/${slug}`).subscribe({
      next: (biz) => {
        this.business = biz;
        this.loading = false;
        this.cdr.detectChanges();
        this.loadMenu(slug);
      },
      error: () => {
        this.error = 'Local no encontrado';
        this.loading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private loadMenu(slug: string): void {
    this.menuLoading = true;
    this.http.get<any>(`/api/v1/public/local/${slug}/menu`).subscribe({
      next: (res) => {
        this.menuLoading = false;
        if (res.success && res.data) {
          this.menuData = res.data;
          this.menuAvailable = true;
          // Convert ExternalMenu format to MenuData format (same as qr-service does)
          this.convertAndStoreMenu(res.data);
        }
        this.cdr.detectChanges();
      },
      error: () => {
        this.menuLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  private convertAndStoreMenu(externalMenu: any): void {
    // Build flat products array with category name
    const products: any[] = [];
    const categories = externalMenu.categories || [];

    for (const cat of categories) {
      if (cat.product_count > 0 || !cat.parent_id) {
        // Products are in the flat products array, matched by category name
      }
    }

    // ExternalMenu already has flat products array with category field
    this.menuData = {
      id: `menu_${externalMenu.company_id}`,
      restaurant_id: String(externalMenu.company_id),
      name: externalMenu.menu_name || externalMenu.display_title,
      description: externalMenu.menu_description,
      currency: externalMenu.currency || 'EUR',
      language: 'es',
      categories: categories.map((c: any) => ({
        ...c,
        id: String(c.id),
        visible: true,
        items: []
      })),
      products: (externalMenu.products || []).map((p: any) => ({
        ...p,
        id: String(p.id),
        category_id: String(categories.find((c: any) => c.name === p.category)?.id || 0),
        image_url: p.image_url || '',
        has_variants: !!(p.variants && p.variants.length > 0),
      })),
      packs: externalMenu.packs || [],
      updated_at: externalMenu.sync_timestamp,
      version: parseInt(externalMenu.version) || 1
    };
  }

  openMenu(): void {
    if (!this.menuData || !this.business) return;

    // Only initialize cart if there's no active cart for this restaurant
    const currentCart = this.cartService.cart();
    if (!currentCart || currentCart.restaurant_id !== this.business.id) {
      this.cartService.initializeCart(
        this.business.id,
        this.business.name,
        ''
      );
    }

    // Save menu data in sessionStorage so it survives login redirect
    sessionStorage.setItem('barclik_public_menu', JSON.stringify({
      restaurant: { id: this.business.id, name: this.business.name },
      menu: this.menuData,
      slug: this.slug
    }));

    this.router.navigate(['/restaurant-menu'], {
      state: {
        restaurant: {
          id: this.business.id,
          name: this.business.name,
        },
        menu: this.menuData,
        source: 'public-business'
      }
    });
  }

  formatHours(): string[] {
    if (!this.business?.hours) return [];
    const days = [
      { name: 'Lunes', data: this.business.hours.monday },
      { name: 'Martes', data: this.business.hours.tuesday },
      { name: 'Miercoles', data: this.business.hours.wednesday },
      { name: 'Jueves', data: this.business.hours.thursday },
      { name: 'Viernes', data: this.business.hours.friday },
      { name: 'Sabado', data: this.business.hours.saturday },
      { name: 'Domingo', data: this.business.hours.sunday },
    ];
    return days.map(d => {
      if (!d.data || d.data.closed) return `${d.name}: Cerrado`;
      const s1 = (d.data.open && d.data.close) ? `${d.data.open}-${d.data.close}` : '';
      const s2 = (d.data.open2 && d.data.close2) ? `${d.data.open2}-${d.data.close2}` : '';
      if (s1 && s2) return `${d.name}: ${s1} / ${s2}`;
      if (s1) return `${d.name}: ${s1}`;
      return `${d.name}: -`;
    });
  }

  get serviceLabels(): string[] {
    if (!this.business) return [];
    const labels: string[] = [];
    if (this.business.allows_dine_in) labels.push('En mesa');
    if (this.business.allows_takeaway) labels.push('Recoger');
    if (this.business.allows_delivery) labels.push('Delivery');
    if (this.business.reservation_enabled) labels.push('Reservas');
    return labels;
  }

  goToLogin(): void {
    sessionStorage.setItem('barclik_return_url', `/local/${this.slug}`);
    this.router.navigate(['/login']);
  }

  goToBusiness(): void {
    if (this.business) {
      this.router.navigate(['/business', this.business.id]);
    }
  }

  callBusiness(): void {
    if (this.business?.phone) {
      window.location.href = `tel:${this.business.phone}`;
    }
  }

  getDirections(): void {
    if (this.business?.address) {
      const q = encodeURIComponent(`${this.business.address}, ${this.business.city || ''}`);
      window.open(`https://maps.google.com/?q=${q}`, '_blank');
    }
  }

  resolveImg(url: string | undefined | null): string {
    return this.configService.resolveImageUrl(url);
  }

  formatPrice(price: number): string {
    return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(price);
  }
}
